import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './SettingsSecrets.css';

// ─── Instance axios raw (bypass l'intercepteur 401 global de useApi.js) ──────
// On en a besoin pour le GET /parametres/secrets initial (qui peut répondre 401
// avec error='2FA_RECENT_REQUIRED' sans qu'on veuille être redirigé vers /login).
const rawApi = axios.create({ baseURL: '/api' });
rawApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem('escamel_token');
  if (token) cfg.headers['Authorization'] = `Bearer ${token}`;
  return cfg;
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const CATEGORIE_LABELS = {
  auth:          'Auth',
  external_api:  'API externe',
  webhook:       'Webhook',
  password_hash: 'Mot de passe',
  smtp:          'SMTP',
};

// Clés qui doivent TOUJOURS être rotatées en paire (sinon l'auth externe casse)
const CLES_COUPLEES = new Set(['WC_KEY', 'WC_SECRET', 'SMTP_USER', 'SMTP_PASS']);
const COUPLE_POUR_CLE = {
  WC_KEY: 'wc', WC_SECRET: 'wc',
  SMTP_USER: 'smtp', SMTP_PASS: 'smtp',
};

function fmtDatetime(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('fr-CA') + ' ' + d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
}

function copier(texte) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(texte).catch(() => {
      const el = document.createElement('textarea');
      el.value = texte;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
  } else {
    const el = document.createElement('textarea');
    el.value = texte;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
}

// ─── Modal 2FA récent ─────────────────────────────────────────────────────────
function Modal2FARecent({ onSuccess }) {
  const [code, setCode]   = useState('');
  const [erreur, setErreur] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  async function soumettre(e) {
    e?.preventDefault();
    if (code.length !== 6) { setErreur('Le code doit contenir 6 chiffres.'); return; }
    setLoading(true);
    setErreur('');
    try {
      await rawApi.post('/auth/verify-2fa-recent', { code });
      onSuccess();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Code invalide ou expiré.');
      setCode('');
      setTimeout(() => inputRef.current?.focus(), 50);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ss-modal-overlay">
      <div className="ss-modal-box" style={{ maxWidth: 360 }}>
        <div className="ss-gate">
          <div className="ss-gate-icon">
            <Icon name="verrou" size={28} />
          </div>
          <h2 className="ss-gate-titre">Acces zone sensible</h2>
          <p className="ss-gate-desc">Entre ton code Google Authenticator pour accéder à la gestion des secrets.</p>
          <form className="ss-gate-form" onSubmit={soumettre}>
            <input
              ref={inputRef}
              className="form-input ss-gate-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoComplete="one-time-code"
              disabled={loading}
            />
            {erreur && <div className="ss-gate-err"><Icon name="erreur" size={13} style={{ marginRight: 4 }} />{erreur}</div>}
            <button className="btn btn-primary btn--block" type="submit" disabled={loading || code.length !== 6}>
              {loading ? 'Vérification…' : 'Valider'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Countdown restart ────────────────────────────────────────────────────────
function RestartCountdown({ dureeS = 60, affecteJwt = false, onDone }) {
  const [restant, setRestant] = useState(dureeS);
  const debut = useRef(Date.now());

  useEffect(() => {
    const iv = setInterval(() => {
      const ecoule = Math.floor((Date.now() - debut.current) / 1000);
      const r = Math.max(0, dureeS - ecoule);
      setRestant(r);
      if (r === 0) {
        clearInterval(iv);
        if (affecteJwt) {
          localStorage.removeItem('escamel_token');
          localStorage.removeItem('escamel_user');
          window.location.href = '/login';
        } else {
          onDone?.();
        }
      }
    }, 500);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const pct = (restant / dureeS) * 100;

  function rafraichirMaintenant() {
    if (affecteJwt) {
      localStorage.removeItem('escamel_token');
      localStorage.removeItem('escamel_user');
      window.location.href = '/login';
    } else {
      window.location.reload();
    }
  }

  return (
    <div className="ss-success-box" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Rotation appliquée. Backend en redémarrage.</div>
      <div className="ss-countdown-wrap">
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
          <span>Disponible dans ~{restant}s</span>
          <span>{Math.round(pct)}%</span>
        </div>
        <div className="ss-countdown-bar-bg">
          <div className="ss-countdown-bar" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <button className="btn btn-ghost" style={{ marginTop: 10, fontSize: 12 }} onClick={rafraichirMaintenant}>
        Rafraîchir maintenant
      </button>
    </div>
  );
}

// ─── ConfirmRestartModal ──────────────────────────────────────────────────────
function ConfirmRestartModal({ cle, onAnnuler, onConfirmer, valeurMasquee }) {
  const affecteJwt = cle === 'JWT_SECRET';
  return (
    <div className="ss-modal-overlay">
      <div className="ss-modal-box" style={{ maxWidth: 420 }}>
        <h3 className="ss-modal-titre">Confirmer la rotation</h3>
        <p className="ss-modal-desc">
          Tu es sur le point de rotater <span style={{ color: 'var(--gold-500)', fontFamily: 'monospace' }}>{cle}</span>.
          {valeurMasquee && <> La nouvelle valeur se terminera par <span style={{ fontFamily: 'monospace', color: 'var(--warning)' }}>{valeurMasquee}</span>.</>}
        </p>
        <div className="ss-warning-box">
          Le backend va redémarrer dans ~3 secondes et sera inaccessible pendant ~60 secondes.
          {affecteJwt && <strong> Tous les tokens LCU/ELA/agent-debug seront invalides — tu seras déconnecté.</strong>}
        </div>
        <div className="ss-modal-actions">
          <button className="btn btn-ghost" onClick={onAnnuler}>Annuler</button>
          <button className="btn btn-danger" onClick={onConfirmer}>Confirmer et rotater</button>
        </div>
      </div>
    </div>
  );
}

// ─── RotationModal ────────────────────────────────────────────────────────────
function RotationModal({ secret, onClose, onSuccess, on2FARequired }) {
  const { key: cle, category } = secret;
  const [etape, setEtape]       = useState(1); // pour TOTP wizard
  const [valeur, setValeur]     = useState('');
  const [valeur2, setValeur2]   = useState(''); // confirm pwd
  const [montrer, setMontrer]   = useState(false);
  const [montrer2, setMontrer2] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated]   = useState(null); // { value, qrcode_data_url, otpauth_url, secret }
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, details }
  const [applying, setApplying]     = useState(false);
  const [erreur, setErreur]         = useState('');
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [restarting, setRestarting]         = useState(false);
  const [affecteJwt, setAffecteJwt]         = useState(false);
  const [totpCode, setTotpCode]             = useState('');
  const [copied, setCopied]                 = useState(false);

  const isRandom    = ['JWT_SECRET', 'GF_WEBHOOK_SECRET', 'AGENT_JWT_TOKEN'].includes(cle);
  const isPwdHash   = category === 'password_hash';
  const isTotp      = category === 'auth' && (cle === 'LCU_TOTP_SECRET' || cle === 'ELA_TOTP_SECRET');
  const isManual    = !isRandom && !isPwdHash && !isTotp;

  function validerMdp(pwd) {
    if (pwd.length < 12) return 'Au moins 12 caractères requis.';
    if (/[#$\\'"]/.test(pwd)) return 'Les caractères # $ \\ \' " sont interdits.';
    return null;
  }

  async function generer() {
    setGenerating(true);
    setErreur('');
    try {
      const res = await rawApi.post(`/parametres/secrets/${cle}/generate`);
      setGenerated(res.data);
      if (isTotp) setEtape(2);
    } catch (e) {
      const err = e.response?.data;
      if (err?.error === '2FA_RECENT_REQUIRED') { on2FARequired(); return; }
      setErreur(e.response?.data?.error || 'Erreur lors de la génération.');
    } finally {
      setGenerating(false);
    }
  }

  async function tester(avecValeur) {
    setTesting(true);
    setTestResult(null);
    setErreur('');
    try {
      const body = {};
      if (avecValeur !== undefined) body.value = avecValeur;
      if (isTotp && generated) {
        body.value = generated.value;
        body.code  = totpCode;
      }
      const res = await rawApi.post(`/parametres/secrets/${cle}/test`, body);
      setTestResult({ ok: res.data.ok, details: res.data.details || '' });
      if (isTotp && res.data.ok) setEtape(3);
    } catch (e) {
      const err = e.response?.data;
      if (err?.error === '2FA_RECENT_REQUIRED') { on2FARequired(); return; }
      setTestResult({ ok: false, details: e.response?.data?.error || 'Erreur réseau' });
    } finally {
      setTesting(false);
    }
  }

  function demanderConfirmation() {
    setErreur('');
    // Validation locale
    if (isPwdHash) {
      const err = validerMdp(valeur);
      if (err) { setErreur(err); return; }
      if (valeur !== valeur2) { setErreur('Les mots de passe ne correspondent pas.'); return; }
    }
    if (isManual && !valeur.trim()) { setErreur('La valeur ne peut pas être vide.'); return; }
    if (isRandom && !generated) { setErreur('Génère une valeur d\'abord.'); return; }
    setAffecteJwt(cle === 'JWT_SECRET');
    setConfirmVisible(true);
  }

  async function appliquer() {
    setConfirmVisible(false);
    setApplying(true);
    setErreur('');
    try {
      let body = {};
      if (isPwdHash)       body = { password: valeur };
      else if (isTotp)     body = { value: generated.value, code: totpCode };
      else if (isRandom)   body = { value: generated.value };
      else                 body = { value: valeur };

      await rawApi.put(`/parametres/secrets/${cle}`, body);
      setRestarting(true);
    } catch (e) {
      const err = e.response?.data;
      if (err?.error === '2FA_RECENT_REQUIRED') { on2FARequired(); return; }
      setErreur(err?.error || 'Erreur lors de l\'application.');
      setApplying(false);
    }
  }

  function handleCopier(txt) {
    copier(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const valeurMasquee = (() => {
    const v = isRandom ? generated?.value : isPwdHash ? '' : valeur;
    if (!v || isPwdHash) return null;
    return '…' + v.slice(-4);
  })();

  if (restarting) {
    return (
      <div className="ss-modal-overlay">
        <div className="ss-modal-box">
          <h3 className="ss-modal-titre">Rotation en cours</h3>
          <RestartCountdown
            dureeS={60}
            affecteJwt={affecteJwt}
            onDone={() => { onSuccess(); onClose(); }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {confirmVisible && (
        <ConfirmRestartModal
          cle={cle}
          valeurMasquee={valeurMasquee}
          onAnnuler={() => setConfirmVisible(false)}
          onConfirmer={appliquer}
        />
      )}

      <div className="ss-modal-overlay">
        <div className="ss-modal-box wide">
          <button className="ss-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
          <h3 className="ss-modal-titre">Rotation — {cle}</h3>
          <p className="ss-modal-desc"><span className="badge ss-badge-cat">{CATEGORIE_LABELS[category] || category}</span></p>

          {/* ── Avertissement special JWT ── */}
          {cle === 'JWT_SECRET' && (
            <div className="ss-warning-box" style={{ marginBottom: 16 }}>
              Cette rotation invalide tous les tokens (LCU, ELA, agent-debug) et régénère AGENT_JWT_TOKEN automatiquement. Tu seras déconnecté après le redémarrage.
            </div>
          )}
          {/* ── Avertissement STRIPE_WEBHOOK_SECRET ── */}
          {cle === 'STRIPE_WEBHOOK_SECRET' && (
            <div className="ss-info-box" style={{ marginBottom: 16 }}>
              N'oublie pas de mettre à jour le "Signing secret" sur dashboard.stripe.com/webhooks (endpoint https://app.escamel.ca/api/stripe/webhook). Sans ça, les webhooks seront rejetés.
            </div>
          )}
          {/* ── Avertissement GF_WEBHOOK_SECRET ── */}
          {cle === 'GF_WEBHOOK_SECRET' && (
            <div className="ss-info-box" style={{ marginBottom: 16 }}>
              N'oublie pas de mettre a jour dans WP Admin → Forms → Webhook → Custom Header <code>x-gf-secret</code> avec la nouvelle valeur.
            </div>
          )}

          {/* ═══ CAS 1 : External API / Webhook manuel ═══ */}
          {isManual && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label className="form-label">Nouvelle valeur</label>
                <div className="ss-input-reveal-wrap">
                  <input
                    className="form-input"
                    type={montrer ? 'text' : 'password'}
                    value={valeur}
                    onChange={e => setValeur(e.target.value)}
                    placeholder="Coller la nouvelle clé ici"
                    autoComplete="off"
                  />
                  <button className="btn btn-ghost btn--sm" style={{ padding: '8px 10px' }} onClick={() => setMontrer(v => !v)}>
                    <Icon name="voir" size={14} />
                  </button>
                </div>
              </div>
              {/* Bouton Tester avec cette valeur */}
              <button
                className="btn btn-ghost"
                onClick={() => tester(valeur)}
                disabled={testing || !valeur.trim()}
                style={{ alignSelf: 'flex-start' }}
              >
                {testing ? 'Test en cours…' : 'Tester avec cette valeur'}
              </button>
              {testResult && (
                <div className={`ss-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
                  {testResult.ok ? <><Icon name="succes" size={13} /> Test réussi</> : <><Icon name="erreur" size={13} /> Erreur : {testResult.details}</>}
                </div>
              )}
              {erreur && <div className="ss-gate-err"><Icon name="erreur" size={13} style={{ marginRight: 4 }} />{erreur}</div>}
              <div className="ss-modal-actions">
                <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
                <button
                  className="btn btn-primary"
                  onClick={demanderConfirmation}
                  disabled={applying || !valeur.trim() || (testResult !== null && !testResult.ok)}
                >
                  {applying ? 'Application…' : 'Appliquer'}
                </button>
              </div>
            </div>
          )}

          {/* ═══ CAS 2 : Random ═══ */}
          {isRandom && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p className="muted" style={{ fontSize: 13 }}>
                Cliquer "Générer" crée une nouvelle valeur aléatoire sécurisée. La valeur actuelle reste active jusqu'à ce que tu cliques "Appliquer".
              </p>
              {!generated ? (
                <button className="btn btn-ghost" onClick={generer} disabled={generating} style={{ alignSelf: 'flex-start' }}>
                  {generating ? 'Génération…' : 'Générer une nouvelle valeur'}
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div className="ss-success-box" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', flex: 1 }}>
                      {generated.value}
                    </span>
                    <button className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => handleCopier(generated.value)}>
                      {copied ? '✓ Copie' : 'Copier'}
                    </button>
                  </div>
                </div>
              )}
              {erreur && <div className="ss-gate-err">{erreur}</div>}
              <div className="ss-modal-actions">
                <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
                <button
                  className="btn btn-primary"
                  onClick={demanderConfirmation}
                  disabled={applying || !generated}
                >
                  {applying ? 'Application…' : 'Appliquer'}
                </button>
              </div>
            </div>
          )}

          {/* ═══ CAS 3 : password_hash ═══ */}
          {isPwdHash && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="ss-info-box" style={{ fontSize: 12 }}>
                Règle : 12+ caractères, éviter # $ \ ' "
              </div>
              <div>
                <label className="form-label">Nouveau mot de passe</label>
                <div className="ss-input-reveal-wrap">
                  <input className="form-input" type={montrer ? 'text' : 'password'} value={valeur} onChange={e => setValeur(e.target.value)} autoComplete="new-password" />
                  <button className="btn btn-ghost btn--sm" style={{ padding: '8px 10px' }} onClick={() => setMontrer(v => !v)}><Icon name="voir" size={14} /></button>
                </div>
              </div>
              <div>
                <label className="form-label">Confirmer le mot de passe</label>
                <div className="ss-input-reveal-wrap">
                  <input className="form-input" type={montrer2 ? 'text' : 'password'} value={valeur2} onChange={e => setValeur2(e.target.value)} autoComplete="new-password" />
                  <button className="btn btn-ghost btn--sm" style={{ padding: '8px 10px' }} onClick={() => setMontrer2(v => !v)}><Icon name="voir" size={14} /></button>
                </div>
              </div>
              {erreur && <div className="ss-gate-err"><Icon name="erreur" size={13} style={{ marginRight: 4 }} />{erreur}</div>}
              <div className="ss-modal-actions">
                <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
                <button
                  className="btn btn-primary"
                  onClick={demanderConfirmation}
                  disabled={applying || valeur.length < 12 || valeur !== valeur2}
                >
                  {applying ? 'Application…' : 'Appliquer'}
                </button>
              </div>
            </div>
          )}

          {/* ═══ CAS 4 : TOTP wizard ═══ */}
          {isTotp && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Barres d'étapes */}
              <div className="ss-wizard-steps">
                {[1, 2, 3].map(s => (
                  <div key={s} className={`ss-wizard-step ${etape > s ? 'done' : etape === s ? 'active' : ''}`} />
                ))}
              </div>
              <div className="muted" style={{ fontSize: 12, textAlign: 'center' }}>
                Étape {etape} / 3 — {['Générer le secret', 'Scanner le QR', 'Valider le code'][etape - 1]}
              </div>

              {/* Étape 1 : Générer */}
              {etape === 1 && (
                <>
                  <p className="muted" style={{ fontSize: 13 }}>
                    Un nouveau secret TOTP sera généré. Ton authenticator actuel continuera de fonctionner jusqu'à ce que tu valides et appliques.
                  </p>
                  <button className="btn btn-ghost" onClick={generer} disabled={generating} style={{ alignSelf: 'flex-start' }}>
                    {generating ? 'Génération…' : 'Générer nouveau secret'}
                  </button>
                  {erreur && <div className="ss-gate-err">{erreur}</div>}
                </>
              )}

              {/* Étape 2 : Scanner */}
              {etape === 2 && generated && (
                <>
                  <div className="ss-qr-wrap">
                    <img src={generated.qrcode_data_url} alt="QR code TOTP" />
                    <div className="ss-secret-b32">{generated.value}</div>
                    <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => handleCopier(generated.value)}>
                      {copied ? '✓ Copié' : 'Copier le code manuel'}
                    </button>
                  </div>
                  <p className="muted" style={{ fontSize: 13, textAlign: 'center' }}>
                    Scanne ce QR dans Google Authenticator ou Authy, puis passe à l'étape suivante.
                  </p>
                  <div className="ss-modal-actions">
                    <button className="btn btn-ghost" onClick={() => setEtape(1)}>Retour</button>
                    <button className="btn btn-primary" onClick={() => setEtape(3)}>Suivant — Valider le code</button>
                  </div>
                </>
              )}

              {/* Étape 3 : Valider + appliquer */}
              {etape === 3 && (
                <>
                  <p className="muted" style={{ fontSize: 13 }}>
                    Entre le code affiché dans ton authenticator pour confirmer que le scan a fonctionné.
                  </p>
                  <div>
                    <label className="form-label">Code à 6 chiffres</label>
                    <input
                      className="form-input ss-gate-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={totpCode}
                      onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      autoFocus
                    />
                  </div>
                  <button
                    className="btn btn-ghost"
                    onClick={() => tester()}
                    disabled={testing || totpCode.length !== 6}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {testing ? 'Test…' : 'Tester le code'}
                  </button>
                  {testResult && (
                    <div className={`ss-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
                      {testResult.ok ? <><Icon name="succes" size={13} /> Code valide</> : <><Icon name="erreur" size={13} /> Code invalide — {testResult.details}</>}
                    </div>
                  )}
                  {erreur && <div className="ss-gate-err"><Icon name="erreur" size={13} style={{ marginRight: 4 }} />{erreur}</div>}
                  <div className="ss-modal-actions">
                    <button className="btn btn-ghost" onClick={() => setEtape(2)}>Retour</button>
                    <button
                      className="btn btn-primary"
                      onClick={demanderConfirmation}
                      disabled={applying || !testResult?.ok || totpCode.length !== 6}
                    >
                      {applying ? 'Application…' : 'Tester & Appliquer'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── RotationModalCouple ──────────────────────────────────────────────────────
// Pour WC_KEY+WC_SECRET et SMTP_USER+SMTP_PASS
function RotationModalCouple({ cle1, cle2, label1, label2, onClose, onSuccess, on2FARequired }) {
  const [val1, setVal1]         = useState('');
  const [val2, setVal2]         = useState('');
  const [show1, setShow1]       = useState(false);
  const [show2, setShow2]       = useState(false);
  const [testing, setTesting]   = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [erreur, setErreur]     = useState('');
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [restarting, setRestarting]         = useState(false);

  async function tester() {
    setTesting(true);
    setTestResult(null);
    setErreur('');
    try {
      // On teste avec la clé 1 (la paire partage la même logique de test WC)
      const res = await rawApi.post(`/parametres/secrets/${cle1}/test`, { value: val1 });
      setTestResult({ ok: res.data.ok, details: res.data.details || '' });
    } catch (e) {
      const err = e.response?.data;
      if (err?.error === '2FA_RECENT_REQUIRED') { on2FARequired(); return; }
      setTestResult({ ok: false, details: err?.error || 'Erreur réseau' });
    } finally {
      setTesting(false);
    }
  }

  async function appliquer() {
    setConfirmVisible(false);
    setApplying(true);
    setErreur('');
    try {
      await rawApi.put('/parametres/secrets/_couple', { keys: { [cle1]: val1, [cle2]: val2 } });
      setRestarting(true);
    } catch (e) {
      const err = e.response?.data;
      if (err?.error === '2FA_RECENT_REQUIRED') { on2FARequired(); return; }
      setErreur(err?.error || 'Erreur lors de l\'application.');
      setApplying(false);
    }
  }

  if (restarting) {
    return (
      <div className="ss-modal-overlay">
        <div className="ss-modal-box">
          <h3 className="ss-modal-titre">Rotation en cours</h3>
          <RestartCountdown dureeS={60} affecteJwt={false} onDone={() => { onSuccess(); onClose(); }} />
        </div>
      </div>
    );
  }

  return (
    <>
      {confirmVisible && (
        <ConfirmRestartModal
          cle={`${cle1} + ${cle2}`}
          valeurMasquee={val1 ? '…' + val1.slice(-4) : null}
          onAnnuler={() => setConfirmVisible(false)}
          onConfirmer={appliquer}
        />
      )}
      <div className="ss-modal-overlay">
        <div className="ss-modal-box wide">
          <button className="ss-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
          <h3 className="ss-modal-titre">Rotation — {cle1} + {cle2}</h3>
          <p className="ss-modal-desc muted">Ces deux clés sont couplées. Elles doivent être rotatées ensemble.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className="form-label">{label1} ({cle1})</label>
              <div className="ss-input-reveal-wrap">
                <input className="form-input" type={show1 ? 'text' : 'password'} value={val1} onChange={e => setVal1(e.target.value)} autoComplete="off" />
                <button className="btn btn-ghost btn--sm" style={{ padding: '8px 10px' }} onClick={() => setShow1(v => !v)}><Icon name="voir" size={14} /></button>
              </div>
            </div>
            <div>
              <label className="form-label">{label2} ({cle2})</label>
              <div className="ss-input-reveal-wrap">
                <input className="form-input" type={show2 ? 'text' : 'password'} value={val2} onChange={e => setVal2(e.target.value)} autoComplete="off" />
                <button className="btn btn-ghost btn--sm" style={{ padding: '8px 10px' }} onClick={() => setShow2(v => !v)}><Icon name="voir" size={14} /></button>
              </div>
            </div>
            {cle1 === 'WC_KEY' && (
              <button className="btn btn-ghost" onClick={tester} disabled={testing || !val1.trim()} style={{ alignSelf: 'flex-start' }}>
                {testing ? 'Test…' : 'Tester la connexion WooCommerce'}
              </button>
            )}
            {testResult && (
              <div className={`ss-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
                {testResult.ok ? <><Icon name="succes" size={13} /> Test réussi</> : <><Icon name="erreur" size={13} /> Erreur : {testResult.details}</>}
              </div>
            )}
            {erreur && <div className="ss-gate-err"><Icon name="erreur" size={13} style={{ marginRight: 4 }} />{erreur}</div>}
            <div className="ss-modal-actions">
              <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
              <button
                className="btn btn-primary"
                onClick={() => setConfirmVisible(true)}
                disabled={applying || !val1.trim() || !val2.trim()}
              >
                {applying ? 'Application…' : 'Appliquer les deux'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── RollbackModal ────────────────────────────────────────────────────────────
function RollbackModal({ onClose, onSuccess }) {
  const [saisie, setSaisie]   = useState('');
  const [rolling, setRolling] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [restarting, setRestarting] = useState(false);
  const valide = saisie === 'ROLLBACK';

  async function executer() {
    if (!valide) return;
    setRolling(true);
    setErreur('');
    try {
      await rawApi.post('/parametres/secrets/rollback', { confirmation: 'ROLLBACK' });
      setRestarting(true);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors du rollback.');
      setRolling(false);
    }
  }

  if (restarting) {
    return (
      <div className="ss-modal-overlay">
        <div className="ss-modal-box">
          <h3 className="ss-modal-titre" style={{ color: 'var(--danger)' }}>Rollback en cours</h3>
          <RestartCountdown dureeS={60} affecteJwt={false} onDone={() => { onSuccess(); onClose(); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="ss-modal-overlay">
      <div className="ss-modal-box">
        <button className="ss-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
        <h3 className="ss-modal-titre" style={{ color: 'var(--danger)' }}>Rollback d'urgence</h3>
        <p className="ss-modal-desc">
          Cette action restaure le backup précédent du .env et redémarre le backend. À utiliser uniquement si une rotation récente a cassé le système.
        </p>
        <div className="ss-danger-box" style={{ marginBottom: 16 }}>
          Toutes les rotations effectuées depuis le dernier backup seront annulées.
        </div>
        <div>
          <label className="form-label">Tape <strong>ROLLBACK</strong> pour confirmer</label>
          <input
            className="form-input"
            type="text"
            value={saisie}
            onChange={e => setSaisie(e.target.value)}
            placeholder="ROLLBACK"
            autoFocus
            autoComplete="off"
          />
        </div>
        {erreur && <div className="ss-gate-err" style={{ marginTop: 8 }}>{erreur}</div>}
        <div className="ss-modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-danger" onClick={executer} disabled={!valide || rolling}>
            {rolling ? 'Rollback…' : 'Exécuter le rollback'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AuditLog ─────────────────────────────────────────────────────────────────
function AuditLog() {
  const [lignes, setLignes]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const LIMIT = 10;

  const charger = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await rawApi.get(`/parametres/secrets/audit?page=${p}&limit=${LIMIT}`);
      const data = res.data || {};
      if (p === 1) {
        setLignes(data.rows || []);
      } else {
        setLignes(prev => [...prev, ...(data.rows || [])]);
      }
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Audit log:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(1); }, [charger]);

  function chargerPlus() {
    const nextPage = page + 1;
    setPage(nextPage);
    charger(nextPage);
  }

  const ACTION_LABELS = {
    rotate:   'Rotation',
    test:     'Test',
    reveal:   'Révélation',
    rollback: 'Rollback',
  };

  return (
    <div className="ss-audit-wrap">
      <h3 className="ss-section-titre">Historique des rotations</h3>
      {lignes.length === 0 && !loading && (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>Aucune entrée dans l'historique.</div>
      )}
      {lignes.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Clé</th>
                <th>Action</th>
                <th>Utilisateur</th>
                <th>Résultat</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map(l => (
                <tr key={l.id}>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }} className="muted">{fmtDatetime(l.created_at)}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--gold-500)' }}>{l.key_name}</td>
                  <td style={{ fontSize: 12 }}>{ACTION_LABELS[l.action] || l.action}</td>
                  <td style={{ fontSize: 12 }}>{l.user_name || '—'}</td>
                  <td>
                    {l.succeeded
                      ? <span className="ss-badge-status ss-badge-ok">OK</span>
                      : <span className="ss-badge-status ss-badge-fail">Échec</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {lignes.length < total && (
        <button className="btn btn-ghost ss-load-more" onClick={chargerPlus} disabled={loading}>
          {loading ? 'Chargement…' : `Charger plus (${lignes.length}/${total})`}
        </button>
      )}
    </div>
  );
}

// ─── SecretRow ────────────────────────────────────────────────────────────────
function SecretRow({ secret, onRotate, on2FARequired, onTestDone }) {
  const { key: cle, category, masked, last_updated, last_test_ok, last_test_at } = secret;
  const [revealed, setRevealed]     = useState(null);  // valeur revelee
  const [countdown, setCountdown]   = useState(0);     // secondes restantes
  const [testing, setTesting]       = useState(false);
  const [localTestOk, setLocalTestOk] = useState(last_test_ok);
  const [localTestAt, setLocalTestAt] = useState(last_test_at);
  const intervalRef = useRef(null);

  async function reveler() {
    if (revealed !== null) return; // deja en cours
    try {
      const res = await rawApi.get(`/parametres/secrets/${cle}/reveal`);
      setRevealed(res.data.value);
      setCountdown(10);
      let restant = 10;
      intervalRef.current = setInterval(() => {
        restant--;
        setCountdown(restant);
        if (restant <= 0) {
          clearInterval(intervalRef.current);
          setRevealed(null);
          setCountdown(0);
        }
      }, 1000);
    } catch (e) {
      const err = e.response?.data;
      if (err?.error === '2FA_RECENT_REQUIRED') { on2FARequired(); return; }
      alert(err?.error || 'Erreur lors de la révélation.');
    }
  }

  useEffect(() => () => clearInterval(intervalRef.current), []);

  async function tester() {
    setTesting(true);
    try {
      const res = await rawApi.post(`/parametres/secrets/${cle}/test`);
      setLocalTestOk(res.data.ok ? 1 : 0);
      setLocalTestAt(new Date().toISOString());
      onTestDone?.(cle, res.data.ok);
    } catch (e) {
      const err = e.response?.data;
      if (err?.error === '2FA_RECENT_REQUIRED') { on2FARequired(); return; }
      setLocalTestOk(0);
      setLocalTestAt(new Date().toISOString());
    } finally {
      setTesting(false);
    }
  }

  const badgeStatus = () => {
    if (localTestAt === null || localTestAt === undefined) {
      return <span className="ss-badge-status ss-badge-none">Jamais testé</span>;
    }
    return localTestOk
      ? <span className="ss-badge-status ss-badge-ok">Testé OK</span>
      : <span className="ss-badge-status ss-badge-fail">Test échoué</span>;
  };

  return (
    <div className="ss-row">
      {/* Nom + catégorie */}
      <div>
        <div className="ss-key">{cle}</div>
        <span className="badge ss-badge-cat" style={{ marginTop: 4, display: 'inline-block' }}>{CATEGORIE_LABELS[category] || category}</span>
      </div>

      {/* Valeur + date + status */}
      <div className="ss-meta">
        {revealed !== null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="ss-reveal-value">{revealed}</span>
            <span className="muted" style={{ fontSize: 11 }}>({countdown}s)</span>
          </div>
        ) : (
          <span className="ss-masked">{masked || '—'}</span>
        )}
        <span className="ss-date">{last_updated ? fmtDatetime(last_updated) : 'Jamais modifié'}</span>
        {badgeStatus()}
      </div>

      {/* Actions */}
      <div className="ss-actions">
        <button
          className="btn btn-ghost"
          style={{ fontSize: 11, padding: '5px 9px' }}
          onClick={reveler}
          disabled={revealed !== null}
          title="Voir la valeur 10 secondes"
        >
          Voir
        </button>
        <button
          className="btn btn-ghost"
          style={{ fontSize: 11, padding: '5px 9px' }}
          onClick={tester}
          disabled={testing}
          title="Tester la connexion"
        >
          {testing ? '…' : 'Tester'}
        </button>
        <button
          className="btn btn-primary"
          style={{ fontSize: 11, padding: '5px 9px' }}
          onClick={() => onRotate(secret)}
          title={CLES_COUPLEES.has(secret.key) ? 'Clés couplées — ouvre le modal de rotation ensemble' : 'Rotater cette clé'}
        >
          {CLES_COUPLEES.has(secret.key) ? 'Rotater (couple)' : 'Rotater'}
        </button>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function SettingsSecrets() {
  const [secrets, setSecrets]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [erreur, setErreur]           = useState('');
  const [need2FA, setNeed2FA]         = useState(false);    // affiche la modal 2FA gate
  const [rotationModal, setRotationModal] = useState(null); // secret en cours de rotation
  const [coupleModal, setCoupleModal]     = useState(null); // 'wc' | 'smtp' | null
  const [rollbackModal, setRollbackModal] = useState(false);
  const [pending2FAAction, setPending2FAAction] = useState(null); // action a rejouer apres 2FA
  const auditRef = useRef(null);

  const chargerSecrets = useCallback(async () => {
    setLoading(true);
    setErreur('');
    try {
      // On utilise rawApi pour intercepter soi-meme le 401 2FA_RECENT_REQUIRED
      const res = await rawApi.get('/parametres/secrets');
      setSecrets(res.data || []);
      setNeed2FA(false);
    } catch (e) {
      const errCode = e.response?.data?.error;
      if (e.response?.status === 401 && errCode === '2FA_RECENT_REQUIRED') {
        setNeed2FA(true);
      } else if (e.response?.status === 401) {
        // Vrai 401 (token expire/absent) → laisser l'intercepteur global gerer
        localStorage.removeItem('escamel_token');
        localStorage.removeItem('escamel_user');
        window.location.href = '/login';
      } else {
        setErreur(e.response?.data?.error || 'Erreur de chargement des secrets.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { chargerSecrets(); }, [chargerSecrets]);

  function handle2FASuccess() {
    setNeed2FA(false);
    chargerSecrets();
    if (pending2FAAction) {
      pending2FAAction();
      setPending2FAAction(null);
    }
  }

  function on2FARequired(retryFn) {
    setPending2FAAction(() => retryFn || null);
    setNeed2FA(true);
  }

  // Grouper par catégorie
  const groupes = {};
  secrets.forEach(s => {
    if (!groupes[s.category]) groupes[s.category] = [];
    groupes[s.category].push(s);
  });

  const ORDRE_CATEGORIES = ['auth', 'external_api', 'webhook', 'password_hash', 'smtp'];

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {[0,1,2].map(i => <div key={i} className="skeleton" style={{ height: 64, borderRadius: 'var(--radius-md)' }} />)}
    </div>
  );

  return (
    <>
      {/* Gate 2FA */}
      {need2FA && <Modal2FARecent onSuccess={handle2FASuccess} />}

      {/* Modal rotation simple */}
      {rotationModal && !need2FA && (
        <RotationModal
          secret={rotationModal}
          onClose={() => setRotationModal(null)}
          onSuccess={() => { setRotationModal(null); chargerSecrets(); }}
          on2FARequired={() => on2FARequired(null)}
        />
      )}

      {/* Modal rotation couple WC */}
      {coupleModal === 'wc' && !need2FA && (
        <RotationModalCouple
          cle1="WC_KEY" cle2="WC_SECRET"
          label1="Consumer Key" label2="Consumer Secret"
          onClose={() => setCoupleModal(null)}
          onSuccess={() => { setCoupleModal(null); chargerSecrets(); }}
          on2FARequired={() => on2FARequired(null)}
        />
      )}

      {/* Modal rotation couple SMTP */}
      {coupleModal === 'smtp' && !need2FA && (
        <RotationModalCouple
          cle1="SMTP_USER" cle2="SMTP_PASS"
          label1="Adresse courriel" label2="Mot de passe application"
          onClose={() => setCoupleModal(null)}
          onSuccess={() => { setCoupleModal(null); chargerSecrets(); }}
          on2FARequired={() => on2FARequired(null)}
        />
      )}

      {/* Modal rollback */}
      {rollbackModal && !need2FA && (
        <RollbackModal
          onClose={() => setRollbackModal(false)}
          onSuccess={() => { setRollbackModal(false); chargerSecrets(); }}
        />
      )}

      <div>
        {erreur && (
          <div className="ss-danger-box" style={{ marginBottom: 16 }}>
            <Icon name="erreur" size={14} style={{ marginRight: 6 }} />{erreur}
          </div>
        )}

        {/* Liste par catégorie */}
        {ORDRE_CATEGORIES.map(cat => {
          const items = groupes[cat];
          if (!items?.length) return null;

          return (
            <div key={cat}>
              <div className="ss-section-titre" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{CATEGORIE_LABELS[cat] || cat}</span>
                {/* Boutons de rotation groupée */}
                {cat === 'external_api' && groupes[cat]?.some(s => s.key === 'WC_KEY') && (
                  <button
                    className="btn btn-ghost ss-couple-btn"
                    onClick={() => setCoupleModal('wc')}
                  >
                    Rotater WC_KEY + WC_SECRET
                  </button>
                )}
                {cat === 'smtp' && (
                  <button
                    className="btn btn-ghost ss-couple-btn"
                    onClick={() => setCoupleModal('smtp')}
                  >
                    Rotater SMTP ensemble
                  </button>
                )}
              </div>

              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {items.map(s => (
                  <SecretRow
                    key={s.key}
                    secret={s}
                    onRotate={(sec) => {
                      // Les clés couplées (WC_KEY, WC_SECRET, SMTP_USER, SMTP_PASS) doivent
                      // passer par le modal couple — le test individuel échoue toujours car
                      // le backend envoie une paire incohérente (nouvelle + ancienne valeur).
                      if (CLES_COUPLEES.has(sec.key)) {
                        setCoupleModal(COUPLE_POUR_CLE[sec.key]);
                      } else {
                        setRotationModal(sec);
                      }
                    }}
                    on2FARequired={() => on2FARequired(null)}
                    onTestDone={(cle, ok) => {
                      setSecrets(prev => prev.map(x =>
                        x.key === cle ? { ...x, last_test_ok: ok ? 1 : 0, last_test_at: new Date().toISOString() } : x
                      ));
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Section audit */}
        <div ref={auditRef}>
          <AuditLog />
        </div>

        {/* Rollback d'urgence */}
        <div className="ss-rollback-section">
          <div>
            <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 13 }}>Rollback d'urgence</div>
            <div className="muted" style={{ fontSize: 12 }}>Restaure le .env de la sauvegarde précédente et redémarre le backend.</div>
          </div>
          <button className="btn btn-danger" style={{ fontSize: 13 }} onClick={() => setRollbackModal(true)}>
            Rollback d'urgence
          </button>
        </div>
      </div>
    </>
  );
}
